import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { colors, fontSize, sxPresets } from '../../theme';

export const thSx = {
  ...sxPresets.tableHead,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  px: 1.5,
  borderBottom: `2px solid ${colors.gray[200]}`,
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
};

export const tdSx = {
  py: 0.75,
  px: 1.5,
  fontSize: fontSize.md,
  borderBottom: `1px solid ${colors.gray[100]}`,
};

export const trSx = {
  cursor: 'pointer',
  '&:nth-of-type(even)': { bgcolor: colors.gray[50] },
  '&:hover': {
    bgcolor: alpha(colors.info.main, 0.05),
    '& td:first-of-type': { borderLeft: `2px solid ${colors.info.main}` },
  },
  transition: 'all 0.15s ease',
};

export const filterControlSx = {
  minWidth: { xs: '100%', sm: 130 },
  '& .MuiOutlinedInput-root': {
    height: 36,
    fontSize: fontSize.sm,
    bgcolor: colors.background.paper,
    '& fieldset': { borderColor: colors.gray[200] },
    '&:hover fieldset': { borderColor: colors.gray[300] },
  },
  '& .MuiInputLabel-root': { fontSize: fontSize.sm },
};

export const Card = ({ children, sx = {} }) => (
  <Box sx={{ ...sxPresets.card, ...sx }}>
    {children}
  </Box>
);

export const CardTitle = ({ children, icon: Icon, action }) => (
  <Box
    sx={{
      ...sxPresets.rowBetween,
      px: 2,
      py: 1.5,
      borderBottom: `1px solid ${colors.gray[100]}`,
      gap: 1,
      flexWrap: 'wrap',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      {Icon ? (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            flexShrink: 0,
            background: `linear-gradient(135deg, ${colors.info.main}, #6366f1)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 16, color: colors.background.paper }} />
        </Box>
      ) : null}
      <Typography sx={{ ...sxPresets.sectionTitle, fontSize: fontSize.base }}>
        {children}
      </Typography>
    </Box>
    {action}
  </Box>
);

export const Tile = ({ label, value, sub, color = colors.info.main, Icon }) => (
  <Box
    sx={{
      bgcolor: colors.background.paper,
      borderRadius: 1.5,
      boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
      border: `1px solid ${colors.gray[100]}`,
      px: 1.5,
      py: 1.25,
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      minWidth: 0,
      transition: 'all .15s ease',
      '&:hover': {
        borderColor: alpha(color, 0.35),
        boxShadow: `0 2px 8px ${alpha(color, 0.12)}`,
      },
    }}
  >
    {Icon ? (
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.2,
          flexShrink: 0,
          background: `linear-gradient(135deg, ${alpha(color, 0.18)}, ${alpha(color, 0.06)})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 18, color }} />
      </Box>
    ) : null}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: fontSize.xs,
          color: alpha(color, 0.75),
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ mt: 0.35, fontSize: '1.35rem', fontWeight: 800, color, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography
          sx={{
            fontSize: fontSize.xs,
            color: colors.gray[400],
            mt: 0.25,
            fontWeight: 500,
            ...sxPresets.truncate,
          }}
        >
          {sub}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

export const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: fontSize.xs,
      fontWeight: 700,
      color: colors.gray[400],
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);
